import * as THREE from 'three';
import { MaterialFamily, GlazingTier } from '../types';
import { MATERIAL_ROUGHNESS, MATERIAL_METALNESS, HANDLE_FINISH_COLORS } from '../utils/colors';
import { PROFILE } from '../utils/profileConstants';

export class MaterialLibrary {
  private cache = new Map<string, THREE.Material>();

  frameMaterial(material: MaterialFamily, color: string): THREE.MeshStandardMaterial {
    const key = `frame-${material}-${color}`;
    if (this.cache.has(key)) return this.cache.get(key) as THREE.MeshStandardMaterial;

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      roughness: MATERIAL_ROUGHNESS[material],
      metalness: MATERIAL_METALNESS[material],
    });
    this.cache.set(key, mat);
    return mat;
  }

  glass(tint?: string, tier?: GlazingTier): THREE.MeshPhysicalMaterial {
    const key = `glass-${tint || 'clear'}-${tier || 'default'}`;
    if (this.cache.has(key)) return this.cache.get(key) as THREE.MeshPhysicalMaterial;

    const thickness = tier === GlazingTier.HR_TRIPLE ? PROFILE.tripleGlassThick
      : tier === GlazingTier.VACUUM ? PROFILE.vacuumGlassThick
      : PROFILE.glassThickness;

    const baseColor = tint || '#c8e8f0';

    const mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(baseColor),
      transparent: true,
      opacity: 0.15,
      roughness: 0.05,
      metalness: 0.0,
      transmission: 0.9,
      ior: 1.5,
      thickness: thickness,
      side: THREE.DoubleSide,
    });
    this.cache.set(key, mat);
    return mat;
  }

  opaquePanel(): THREE.MeshStandardMaterial {
    const key = 'opaque-panel';
    if (this.cache.has(key)) return this.cache.get(key) as THREE.MeshStandardMaterial;
    const mat = new THREE.MeshStandardMaterial({ color: 0xf5f5f5, roughness: 0.5, metalness: 0.0 });
    this.cache.set(key, mat);
    return mat;
  }

  handleMaterial(finish: string): THREE.MeshStandardMaterial {
    const key = `handle-${finish}`;
    if (this.cache.has(key)) return this.cache.get(key) as THREE.MeshStandardMaterial;

    const color = HANDLE_FINISH_COLORS[finish] || '#c0c0c0';
    const isMetallic = finish !== 'white';
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      roughness: isMetallic ? 0.2 : 0.4,
      metalness: isMetallic ? 0.9 : 0.0,
    });
    this.cache.set(key, mat);
    return mat;
  }

  roedenMaterial(color?: string): THREE.MeshStandardMaterial {
    const c = color || '#ffffff';
    const key = `roeden-${c}`;
    if (this.cache.has(key)) return this.cache.get(key) as THREE.MeshStandardMaterial;
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(c),
      roughness: 0.3,
      metalness: 0.0,
    });
    this.cache.set(key, mat);
    return mat;
  }

  clearCache(): void {
    this.cache.forEach(m => m.dispose());
    this.cache.clear();
  }

  dispose(): void {
    this.clearCache();
  }
}
